import React, {ReactElement, useState, useCallback} from 'react';
import block from 'bem-cn-lite';
import {Scrollspy} from '@doc-tools/components';
import {Foldable} from '@yandex-data-ui/page-constructor';

import withRouter, {WithRouterProps} from 'hoc/withRouter';
import ToggleArrow from 'components/ToggleArrow/ToggleArrow';
import {HEADER_HEIGHT} from '../../../../constants';

import './Menu.scss';

export interface MenuItem {
    text: string | ReactElement;
    url: string;
    desktopOnly?: boolean;
}

interface OwnProps {
    items: MenuItem[];
    className?: string;
    onSectionClick?: () => void;
}

type FullProps = WithRouterProps & OwnProps;

const b = block('menu-block');
const ARROW_SIZE = 14;

const Menu: React.FC<FullProps> = ({
    items,
    className,
    router,
    onSectionClick: onClick,
    children,
}) => {
    const mobileItems = items.filter(({desktopOnly}) => !desktopOnly);
    const [isOpened, setIsOpened] = useState(false);
    const toggleMenu = useCallback(() => {
        setIsOpened(!isOpened);
    }, [isOpened]);
    const onSectionClick = useCallback(() => {
        setIsOpened(false);
        if (onClick) {
            onClick();
        }
    }, [onClick]);

    const getContent = (menuItems: MenuItem[]) => {
        const hashedLinks = menuItems.map((link) => ({
            ...link,
            url: `#${link.url}`,
            hash: `#${link.url}`,
        }));

        return hashedLinks.length > 1 ? (
            <Scrollspy
                className={b('sections', {['mobile-hidden']: !isOpened})}
                currentClassName={b('section', {active: true})}
                items={hashedLinks.map(({hash}) => hash)}
                headerHeight={HEADER_HEIGHT}
                router={router}
                onSectionClick={onSectionClick}
            >
                {hashedLinks.map(({text, url, hash}) => {
                    return (
                        <li key={hash} data-hash={hash} className={b('section', {child: true})}>
                            <a href={url} className={b('section-link')} data-router-shallow>
                                {text}
                            </a>
                        </li>
                    );
                })}
            </Scrollspy>
        ) : null;
    };

    return (
        <menu className={b(null, className)} onClick={toggleMenu}>
            <div className={b('content')}>{children}</div>
            <div className={b('desktop')}>{getContent(items)}</div>
            <Foldable
                // force recalculate height on items count change
                // (for hidden loadables, demo and calculator blocks)
                key={items.length}
                className={b('mobile')}
                isOpened={isOpened}
            >
                {getContent(mobileItems)}
            </Foldable>
            <ToggleArrow
                className={b('arrow')}
                size={ARROW_SIZE}
                type={'vertical'}
                iconType="navigation"
                open={isOpened}
            />
        </menu>
    );
};

export default withRouter(Menu);
